import { FileDropZone } from '@components/tailwind-components/Upload/FileDropZone';
import type {
  TReportIssueAttachments,
  TReportIssueParams,
} from '@conversed/rest/src/contracts/llm-usages/report-issue';
import type { TFile } from '@conversed/shared/src/central/types/common.type';
import {
  removeBase64Prefix,
  stringToArrayBuffer,
} from '@conversed/shared/src/helpers/data.helpers';
import { toastErrorTranslated } from '@helpers/toast';
import { useFileDownload } from '@hooks/useFileDownload';

import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface AttachmentSectionProps {
  reportIssueParams: TReportIssueParams;
  setReportIssueParams: (params: TReportIssueParams) => void;
  accept?: string[];
}

const DEFAULT_FILE_SIZE_LIMIT_MB = 25;

const DEFAULT_ACCEPT = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'video/mp4',
  // * Introduce plenty of issues and edge-cases + they were not needed by the ticket originally
  // 'video/x-matroska',
  // 'video/quicktime',
];

const convertAcceptToExtension = (accept: string) => {
  switch (accept) {
    case 'application/pdf':
      return '.pdf';
    case 'image/jpeg':
      return '.jpg';
    case 'image/png':
      return '.png';
    case 'video/mp4':
      return '.mp4';
    case 'video/x-matroska':
      return '.mkv';
    case 'video/quicktime':
      return '.mov';
    default:
      return '';
  }
};

const FactCheckingAttachmentSection: React.FC<AttachmentSectionProps> = ({
  reportIssueParams,
  setReportIssueParams,
  accept = DEFAULT_ACCEPT,
}) => {
  const { t } = useTranslation();
  const { download } = useFileDownload();

  const [selectedFiles, setSelectedFiles] = useState<TFile[]>([]);

  const fileTypes = useMemo(() => {
    return accept
      .map((mimeType) => convertAcceptToExtension(mimeType))
      .join(' ');
  }, [accept]);

  const files = useMemo(() => {
    return reportIssueParams.attachments || [];
  }, [reportIssueParams.attachments]);

  const currentFileSize = useMemo(
    () =>
      files.reduce((fileSize, { file }) => {
        return typeof file !== 'string' ? file.size + fileSize : fileSize;
      }, 0),
    [files],
  );

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFilesSelect = useCallback(
    (files: TFile[]) => {
      const attachments = reportIssueParams.attachments || [];

      // Take only the first file since we only support single file
      const file = files[0];
      if (!file) return;

      // * Handle custom file size validation externally from FileDropZone
      if (
        file.size + currentFileSize >
        DEFAULT_FILE_SIZE_LIMIT_MB * 1024 * 1024
      ) {
        toastErrorTranslated('data-sources.upload.invalid-file-sizes', {
          limit: DEFAULT_FILE_SIZE_LIMIT_MB,
        });
        return;
      }

      setReportIssueParams({
        ...reportIssueParams,
        attachments: [...attachments, { fileName: file.name, file }],
      });
      setSelectedFiles([]);
    },
    [currentFileSize, reportIssueParams, setReportIssueParams],
  );

  const downloadFile = useCallback(
    ({ file, fileName }: TReportIssueAttachments) => {
      if (typeof file === 'string') {
        return window.open(file, '_blank');
      }

      const { base64, type: fileType } = file;
      const data = stringToArrayBuffer(window.atob(removeBase64Prefix(base64)));

      return download({
        fileName,
        data,
        fileType,
      });
    },
    [download],
  );

  const handleFileRemove = useCallback(
    (id: number) => {
      const attachments = reportIssueParams.attachments || [];

      setReportIssueParams({
        ...reportIssueParams,
        attachments: attachments.filter((_, index) => index !== id),
      });
    },
    [reportIssueParams, setReportIssueParams],
  );

  return (
    <div className='fact-checking-attachment'>
      <h2 className='fact-checking-attachment__title'>
        {t('train-ai.fact-checking.report-issue.attachment.title')}
      </h2>

      <FileDropZone
        fileInputRef={fileInputRef}
        accept={accept}
        selectedFiles={selectedFiles}
        onFilesSelect={handleFilesSelect}
        onFileRemove={() => {}}
        fileSizeLimitMB={DEFAULT_FILE_SIZE_LIMIT_MB}
        className='mb-2'
        multiple={false}
      />

      <div className='fact-checking-attachment__file-types'>
        {t(
          'train-ai.fact-checking.report-issue.attachment.supported-file-types',
          { fileTypes, limit: DEFAULT_FILE_SIZE_LIMIT_MB },
        )}
      </div>

      {files.length > 0 && (
        <div className='fact-checking-attachment__files'>
          {files.map((file, idx) => {
            return (
              <div
                key={idx}
                className='fact-checking-attachment__files__display'>
                <p
                  onClick={() => downloadFile(file)}
                  className='fact-checking-attachment__files__display__text'>
                  {file.fileName}
                </p>
                <button
                  type='button'
                  onClick={() => handleFileRemove(idx)}
                  className='fact-checking-attachment__files__display__button'>
                  ✕
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FactCheckingAttachmentSection;
